import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  People,
  Business,
  LocalOffer,
  AttachMoney,
  PhoneAndroid,
  Analytics,
  LocationOn,
  Schedule,
  Star,
  Refresh,
  GetApp,
  FilterList,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { VenueAdmin, Venue, VenueAnalyticsEvent, Stats, FirebaseFunctionResponse } from '../../types/SuperAdminTypes';

interface AnalyticsData {
  platformOverview: {
    totalRevenue: number;
    revenueGrowth: number;
    totalUsers: number;
    userGrowth: number;
    totalVenues: number;
    venueGrowth: number;
    totalOffers: number;
    offerGrowth: number;
    conversionRate: number;
    averageRedemptions: number;
  };
  revenueMetrics: {
    monthlyRevenue: number;
    subscriptionRevenue: number;
    averageRevenuePerAdmin: number;
    churnRate: number;
    planDistribution: Array<{
      planId: string;
      planName: string;
      count: number;
      revenue: number;
      percentage: number;
    }>;
    revenueTimeline: Array<{
      month: string;
      revenue: number;
      subscribers: number;
    }>;
  };
  userAnalytics: {
    mobileUsers: {
      total: number;
      authenticated: number;
      guests: number;
      activeToday: number;
      retention7Day: number;
      retention30Day: number;
    };
    geographicDistribution: Array<{
      state: string;
      admins: number;
      venues: number;
      offers: number;
    }>;
  };
  venuePerformance: {
    topVenues: Array<{
      id: string;
      name: string;
      adminName: string;
      totalRedemptions: number;
      totalViews: number;
      totalSearches: number;
      totalFavorites: number;
      totalInteractions: number;
      monthlyGrowth: number;
      rating: number;
      location: string;
    }>;
    adminActivity: Array<{
      id: string;
      name: string;
      company: string;
      venueCount: number;
      totalOffers: number;
      lastLogin: string;
      status: string;
    }>;
  };
  offerAnalytics: {
    totalOffers: number;
    activeOffers: number;
    totalRedemptions: number;
    averageRedemptionRate: number;
    topCategories: Array<{
      category: string;
      count: number;
      redemptions: number;
      successRate: number;
    }>;
    topUsers: Array<{
      userId: string;
      userName: string;
      userEmail: string;
      redemptionCount: number;
    }>;
    topVenuesByRedemptions: Array<{
      venueId: string;
      venueName: string;
      redemptionCount: number;
    }>;
    recentPerformance: Array<{
      id: string;
      title: string;
      venueName: string;
      views: number;
      redemptions: number;
      conversionRate: number;
      status: string;
    }>;
  };
  analyticsEvents: {
    totalEvents: number;
    totalViews: number;
    totalSearches: number;
    totalFavorites: number;
    recentActivity: VenueAnalyticsEvent[];
  };
}

interface DateRange {
  start: Date;
  end: Date;
}

const SuperAdminAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>('30d');
  const [selectedMetric, setSelectedMetric] = useState<string>('overview');
  const [customStart, setCustomStart] = useState<Dayjs | null>(null);
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  // Calculate date range based on selected time range
  const getDateRange = (): DateRange => {
    // Custom date range
    if (timeRange === 'custom' && customStart && customEnd) {
      return { start: customStart.toDate(), end: customEnd.toDate() };
    }

    const end = new Date();
    const start = new Date();

    switch (timeRange) {
      case '7d':  start.setDate(end.getDate() - 7); break;
      case '30d': start.setDate(end.getDate() - 30); break;
      case '90d': start.setDate(end.getDate() - 90); break;
      case '1y':  start.setFullYear(end.getFullYear() - 1); break;
      case 'all': start.setFullYear(2025, 0, 1); break;
      default:    start.setDate(end.getDate() - 30);
    }

    return { start, end };
  };

  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value);
    setShowCustomPicker(value === 'custom');
    if (value !== 'custom') {
      setCustomStart(null);
      setCustomEnd(null);
    }
  };

  const handleApplyCustomRange = () => {
    if (customStart && customEnd) {
      loadAnalyticsData();
    }
  };

  // Load analytics data
  useEffect(() => {
    if (timeRange !== 'custom') loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    const startTime = performance.now(); // Track performance
    
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading analytics data...');
      const dateRange = getDateRange();
      const startTimestamp = Timestamp.fromDate(dateRange.start);
      const endTimestamp = Timestamp.fromDate(dateRange.end);
      
      console.log('📅 Date range:', dateRange.start.toISOString(), 'to', dateRange.end.toISOString());

      // Fetch all required data in parallel
      const [
        venueAdmins,
        venues,
        analyticsEvents,
        mobileUsers,
        offersData,
        offerRedemptions,
      ] = await Promise.all([
        fetchVenueAdmins(),
        fetchVenues(),
        fetchAnalyticsEvents(startTimestamp, endTimestamp),
        fetchMobileUsers(),
        fetchOffers(),
        fetchOfferRedemptions(startTimestamp, endTimestamp), // OPTIMIZED: Pass date range
      ]);

      console.log('📊 Data fetched:', venueAdmins.length, 'admins,', venues.length, 'venues,', analyticsEvents.length, 'events');

      // Fetch plan prices here (async context)
      let planPrices: { [key: string]: number } = {};
      try {
        const plansSnapshot = await getDocs(query(
          collection(db, 'admin_subscription_plans'),
          where('isActive', '==', true)
        ));
        plansSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.planId && data.priceMonthly) {
            planPrices[data.planId] = data.priceMonthly / 100;
          }
        });
      } catch (err) {
        console.error('Error loading plan prices:', err);
      }

      // Process the data
      const processedData = processAnalyticsData(
        venueAdmins,
        venues,
        analyticsEvents,
        mobileUsers,
        dateRange,
        offersData,
        offerRedemptions,
        planPrices
      );

      console.log('✅ Analytics loaded in', ((performance.now() - startTime) / 1000).toFixed(2) + 's');
      setAnalyticsData(processedData);
    } catch (err) {
      console.error('❌ Error loading analytics data:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchVenueAdmins = async (): Promise<VenueAdmin[]> => {
    const adminsSnapshot = await getDocs(query(collection(db, 'venue_admins'), limit(200)));
    console.log(`👥 Fetched ${adminsSnapshot.size} venue admins from venue_admins collection`);
    return adminsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as VenueAdmin[];
  };

  const fetchVenues = async (): Promise<Venue[]> => {
    const venuesSnapshot = await getDocs(query(collection(db, 'venues'), limit(500)));
    return venuesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Venue[];
  };

  const fetchOffers = async () => {
    try {
      const offersSnapshot = await getDocs(query(collection(db, 'offers'), limit(500)));
      const allOffers = offersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const activeOffers = allOffers.filter((offer: any) => offer.status === 'active').length;
      const expiredOffers = allOffers.filter((offer: any) => offer.status === 'expired').length;
      return { total: allOffers.length, active: activeOffers, expired: expiredOffers, offers: allOffers };
    } catch (error) {
      console.error('Error fetching offers:', error);
      return { total: 0, active: 0, expired: 0, offers: [] };
    }
  };

  const fetchOfferRedemptions = async (startTimestamp: Timestamp, endTimestamp: Timestamp) => {
    try {
      const redemptionsQuery = query(
        collection(db, 'offer_redemptions'),
        where('redeemedAt', '>=', startTimestamp),
        where('redeemedAt', '<=', endTimestamp),
        orderBy('redeemedAt', 'desc'),
        limit(500)
      );
      const redemptionsSnapshot = await getDocs(redemptionsQuery);
      return redemptionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error: any) {
      console.error('Error fetching offer redemptions:', error);
      if (error.message?.includes('index')) {
        console.error('📋 Index required on offer_redemptions: redeemedAt (Asc)');
      }
      // Capped fallback — don't fetch unbounded
      try {
        const fallbackSnapshot = await getDocs(query(
          collection(db, 'offer_redemptions'),
          orderBy('redeemedAt', 'desc'),
          limit(500)
        ));
        const allRedemptions = fallbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        return allRedemptions.filter((r: any) => {
          if (!r.redeemedAt) return false;
          const d = r.redeemedAt.toDate();
          return d >= startTimestamp.toDate() && d <= endTimestamp.toDate();
        });
      } catch {
        return [];
      }
    }
  };

  const fetchAnalyticsEvents = async (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp
  ): Promise<VenueAnalyticsEvent[]> => {
    try {
      const eventsQuery = query(
        collection(db, 'venue_analytics'),
        where('timestamp', '>=', startTimestamp),
        where('timestamp', '<=', endTimestamp),
        orderBy('timestamp', 'desc'),
        limit(200) // Reduced from 1000 — enough for meaningful analytics
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      return eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VenueAnalyticsEvent));
    } catch (error: any) {
      console.warn('Error fetching with date filter, trying without:', error.message);
      try {
        const eventsSnapshot = await getDocs(query(
          collection(db, 'venue_analytics'),
          orderBy('timestamp', 'desc'),
          limit(200)
        ));
        const allEvents = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VenueAnalyticsEvent));
        return allEvents.filter(event => {
          if (!event.timestamp) return false;
          const eventDate = event.timestamp.toDate();
          return eventDate >= startTimestamp.toDate() && eventDate <= endTimestamp.toDate();
        });
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        return [];
      }
    }
  };

  const fetchMobileUsers = async () => {
    try {
      // Use limit-based sampling to avoid loading all user docs
      // Count users with a profile field set (registered) vs total
      const [totalSnap, registeredSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), limit(2000))),
        getDocs(query(collection(db, 'users'), where('email', '!=', ''), limit(2000))),
      ]);
      const total = totalSnap.size;
      const authenticated = registeredSnap.size;
      return {
        total,
        authenticated,
        guests: Math.max(0, total - authenticated),
      };
    } catch (error) {
      console.error('Error fetching mobile users:', error);
      try {
        const totalSnapshot = await getDocs(query(collection(db, 'users'), limit(2000)));
        return { total: totalSnapshot.size, authenticated: 0, guests: totalSnapshot.size };
      } catch {
        return { total: 0, authenticated: 0, guests: 0 };
      }
    }
  };

  const processAnalyticsData = (
    venueAdmins: VenueAdmin[],
    venues: Venue[],
    analyticsEvents: VenueAnalyticsEvent[],
    mobileUsers: { total: number; authenticated: number; guests: number },
    dateRange: DateRange,
    offersData: { total: number; active: number; expired: number; offers: any[] },
    offerRedemptions: any[],
    planPrices: { [key: string]: number }
  ): AnalyticsData => {
    const activeVenueAdmins = venueAdmins.filter(admin => admin.status === 'active');
    
    const registeredVenues = venues.filter(venue => 
      venue.ownership && 
      venue.ownership.adminId && 
      venue.ownership.adminId.trim() !== '' &&
      !venue.ownership.adminId.includes('_admin')
    );
    
    const activeVenues = registeredVenues.filter(venue => 
      venue.status === 'active' || venue.status === 'verified' || venue.status === 'public'
    );

    const totalOffers = offersData.total;
    const activeOffers = offersData.active;

    const totalRedemptions = offersData.offers.reduce((sum: number, offer: any) => 
      sum + (offer.analytics?.redemptions || 0), 0
    );

    const totalOfferViews = offersData.offers.reduce((sum: number, offer: any) => 
      sum + (offer.analytics?.views || 0), 0
    );

    const validEvents = analyticsEvents.filter(e => e && e.action && e.venueId);
    const viewEvents = validEvents.filter(e => e.action === 'view');
    const searchEvents = validEvents.filter(e => e.action === 'search');
    const favoriteEvents = validEvents.filter(e => e.action === 'favorite');

    const planDistribution = calculatePlanDistribution(activeVenueAdmins, planPrices);
    const monthlyRevenue = planDistribution.reduce((sum, plan) => sum + plan.revenue, 0);

    // Calculate geographic distribution
    const geographicDistribution = calculateGeographicDistribution(venues, venueAdmins);

    // Get top performing venues
    const topVenues = getTopVenues(venues, validEvents);

    // Get admin activity
    const adminActivity = getAdminActivity(venueAdmins, venues);

    // Calculate offer categories from actual offers
    const topCategories = calculateTopCategoriesFromOffers(offersData.offers, venues);

    // Get recent top performing offers
    const recentPerformance = getRecentTopOffersFromOffers(offersData.offers, venues);

    // Calculate top users by redemption count from offer_redemptions
    const topUsers = calculateTopUsersByRedemptions(offerRedemptions);

    // Calculate top venues by redemption count from offer_redemptions
    const topVenuesByRedemptions = calculateTopVenuesByRedemptions(offerRedemptions, venues);

    return {
      platformOverview: {
        totalRevenue: monthlyRevenue,
        revenueGrowth: 0, // Would need historical data to calculate
        totalUsers: mobileUsers.total,
        userGrowth: 0, // Would need historical data to calculate
        totalVenues: registeredVenues.length, // FIXED: Only count registered venues
        venueGrowth: 0, // Would need historical data to calculate
        totalOffers: totalOffers, // FIXED: From actual offers collection
        offerGrowth: 0, // Would need historical data to calculate
        conversionRate: totalOfferViews > 0 ? (totalRedemptions / totalOfferViews) * 100 : 0, // FIXED: Views to redemptions
        averageRedemptions: totalOffers > 0 ? totalRedemptions / totalOffers : 0,
      },
      revenueMetrics: {
        monthlyRevenue,
        subscriptionRevenue: monthlyRevenue,
        averageRevenuePerAdmin: activeVenueAdmins.length > 0 ? monthlyRevenue / activeVenueAdmins.length : 0,
        churnRate: 0,
        planDistribution,
        revenueTimeline: calculateRevenueTimeline(venueAdmins, planPrices, 12),
      },
      userAnalytics: {
        mobileUsers: {
          total: mobileUsers.total,
          authenticated: mobileUsers.authenticated,
          guests: mobileUsers.guests,
          activeToday: 0, // Would need activity tracking
          retention7Day: 0, // Would need historical data
          retention30Day: 0, // Would need historical data
        },
        geographicDistribution,
      },
      venuePerformance: {
        topVenues,
        adminActivity,
      },
      offerAnalytics: {
        totalOffers: totalOffers,
        activeOffers: activeOffers,
        totalRedemptions,
        averageRedemptionRate: totalOffers > 0 ? (totalRedemptions / totalOffers) * 100 : 0,
        topCategories,
        topUsers,
        topVenuesByRedemptions,
        recentPerformance,
      },
      analyticsEvents: {
        totalEvents: validEvents.length,
        totalViews: viewEvents.length,
        totalSearches: searchEvents.length,
        totalFavorites: favoriteEvents.length, // Time-filtered events only, no mixing with all-time venue totals
        recentActivity: [], // Removed — not useful at this granularity
      },
    };
  };

  const calculateRevenueTimeline = (
    venueAdmins: VenueAdmin[],
    planPrices: { [key: string]: number },
    monthsBack: number = 12
  ) => {
    const months: Array<{ month: string; revenue: number; subscribers: number }> = [];
    const now = new Date();

    for (let i = monthsBack - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
      const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
      const label = d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });

      // Count admins who had an active subscription during this month
      let revenue = 0;
      let subscribers = 0;

      venueAdmins.forEach(admin => {
        const sub = admin.subscription as any;
        if (!sub || sub.status === 'pending') return;

        // Use subscription startDate or admin createdAt as subscription start
        const startRaw = sub.startDate || sub.currentPeriodStart || admin.createdAt;
        if (!startRaw) return;
        const start = startRaw.toDate ? startRaw.toDate() : new Date(startRaw);

        // Use canceledAt or renewalDate as end, default to far future if still active
        const endRaw = sub.canceledAt || sub.currentPeriodEnd || null;
        const end = endRaw
          ? (endRaw.toDate ? endRaw.toDate() : new Date(endRaw))
          : new Date(2099, 0, 1);

        // Was this admin subscribed during this month?
        if (start <= monthEnd && end >= monthStart) {
          const price = planPrices[sub.planId] || 0;
          revenue += price;
          if (price > 0) subscribers++;
        }
      });

      months.push({ month: label, revenue, subscribers });
    }

    return months;
  };

  const calculatePlanDistribution = (
    venueAdmins: VenueAdmin[],
    planPrices: { [key: string]: number }
  ) => {
    // Build plan counts dynamically from actual planIds in the data
    const planCounts: { [key: string]: { name: string; count: number } } = {};

    venueAdmins.forEach(admin => {
      const planId = admin.subscription?.planId;
      if (!planId || admin.subscription?.status !== 'active') return;
      if (!planCounts[planId]) {
        // Format planId as display name e.g. 'non_licenced_monthly' → 'Non Licenced'
        const name = planId
          .replace(/_monthly$/, '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c: string) => c.toUpperCase());
        planCounts[planId] = { name, count: 0 };
      }
      planCounts[planId].count++;
    });

    const totalRevenue = Object.entries(planCounts).reduce(
      (sum, [planId, data]) => sum + (data.count * (planPrices[planId] || 0)), 0
    );

    return Object.entries(planCounts).map(([planId, data]) => ({
      planId,
      planName: data.name,
      count: data.count,
      revenue: data.count * (planPrices[planId] || 0),
      percentage: totalRevenue > 0 ? (data.count * (planPrices[planId] || 0) / totalRevenue) * 100 : 0,
    }));
  };

  const calculateGeographicDistribution = (
    venues: Venue[],
    venueAdmins: VenueAdmin[]
  ) => {
    const stateData: { [key: string]: { venues: number; offers: number; admins: Set<string> } } = {};
    
    // Create a Set of valid admin IDs from the venue_admins collection
    // This excludes placeholder admins created when users search venues in the app
    const validAdminIds = new Set(venueAdmins.map(admin => admin.adminId));
    console.log(`📋 Valid admin IDs from venue_admins collection: ${validAdminIds.size}`);

    venues.forEach(venue => {
      let state = 'Unknown';
      
      // Try to extract state from address
      if (venue.address && typeof venue.address === 'string') {
        const addressMatch = venue.address.match(/\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\b/i);
        if (addressMatch) {
          state = addressMatch[1].toUpperCase();
        }
      }
      
      // Fallback to location.formattedAddress
      if (state === 'Unknown' && venue.location?.formattedAddress) {
        const addressMatch = venue.location.formattedAddress.match(/\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\b/i);
        if (addressMatch) {
          state = addressMatch[1].toUpperCase();
        }
      }

      if (!stateData[state]) {
        stateData[state] = { venues: 0, offers: 0, admins: new Set() };
      }

      stateData[state].venues++;
      stateData[state].offers += venue.analytics?.totalOffers || 0;
      
      // FIXED: Only track admins that exist in venue_admins collection
      // This excludes placeholder admins created during venue search
      if (venue.ownership?.adminId && validAdminIds.has(venue.ownership.adminId)) {
        stateData[state].admins.add(venue.ownership.adminId);
      }
    });

    // Also count admins by their address from venue_admins collection
    venueAdmins.forEach(admin => {
      if (admin.address && admin.address.state) {
        const state = admin.address.state.toUpperCase();
        if (!stateData[state]) {
          stateData[state] = { venues: 0, offers: 0, admins: new Set() };
        }
        stateData[state].admins.add(admin.adminId);
      }
    });
    
    return Object.entries(stateData)
      .map(([state, data]) => ({
        state,
        admins: data.admins.size, // Only counts real venue admins
        venues: data.venues,
        offers: data.offers,
      }))
      .sort((a, b) => b.admins - a.admins)
      .filter(loc => loc.state !== 'Unknown' || loc.venues > 0); // Keep Unknown only if it has venues
  };

  const getTopVenues = (
    venues: Venue[],
    analyticsEvents: VenueAnalyticsEvent[]
  ) => {
    // Build per-venue event counts from the time-filtered events
    const eventCounts: { [key: string]: { views: number; searches: number; favorites: number } } = {};
    analyticsEvents.forEach(event => {
      if (!eventCounts[event.venueId]) {
        eventCounts[event.venueId] = { views: 0, searches: 0, favorites: 0 };
      }
      if (event.action === 'view')     eventCounts[event.venueId].views++;
      if (event.action === 'search')   eventCounts[event.venueId].searches++;
      if (event.action === 'favorite') eventCounts[event.venueId].favorites++;
    });

    return venues
      .map(venue => {
        const events = eventCounts[venue.id] || { views: 0, searches: 0, favorites: 0 };

        // Use time-filtered event counts as primary source.
        // Fall back to venue's stored totals only if no events found at all.
        const hasEventData = events.views + events.searches + events.favorites > 0;
        const totalViews     = hasEventData ? events.views     : (venue.totalViews     || 0);
        const totalSearches  = hasEventData ? events.searches  : (venue.totalSearches  || 0);
        const totalFavorites = hasEventData ? events.favorites : (venue.totalFavorites || 0);
        const totalInteractions = totalViews + totalSearches + totalFavorites;

        let locationString = 'Unknown';
        if (venue.location?.formattedAddress) {
          locationString = venue.location.formattedAddress;
        } else if (venue.address && typeof venue.address === 'string') {
          locationString = venue.address;
        }

        return {
          id: venue.id,
          name: venue.businessName || venue.name || venue.tradingName || 'Unknown',
          adminName: venue.ownership?.adminName || 'Unknown',
          totalRedemptions: venue.analytics?.totalRedemptions || 0,
          totalViews,
          totalSearches,
          totalFavorites,
          totalInteractions,
          monthlyGrowth: 0,
          rating: (venue as any).rating || 0,
          location: locationString,
        };
      })
      .filter(v => v.totalInteractions > 0)
      .sort((a, b) => b.totalInteractions - a.totalInteractions)
      .slice(0, 10);
  };

  const getAdminActivity = (venueAdmins: VenueAdmin[], venues: Venue[]) => {
    return venueAdmins
      .map(admin => {
        const adminVenues = venues.filter(v => v.ownership?.adminId === admin.adminId);
        const totalOffers = adminVenues.reduce((sum, v) => 
          sum + (v.analytics?.totalOffers || 0), 0
        );

        return {
          id: admin.id,
          name: admin.name,
          company: admin.company,
          venueCount: adminVenues.length,
          totalOffers,
          lastLogin: admin.updatedAt?.toDate?.()?.toISOString()?.split('T')[0] || 'Unknown',
          status: admin.status,
        };
      })
      .sort((a, b) => b.totalOffers - a.totalOffers)
      .slice(0, 10);
  };

  const calculateTopCategoriesFromOffers = (offers: any[], venues: Venue[]) => {
    // Create a map of venue types/categories
    const categoryData: { 
      [key: string]: { 
        count: number; 
        redemptions: number; 
        totalOffers: number;
      } 
    } = {};

    // Get venue lookup for category info
    const venueMap = new Map(venues.map(v => [v.id, v]));

    offers.forEach((offer: any) => {
      const venue = venueMap.get(offer.venueId);
      const redemptions = offer.analytics?.redemptions || 0;
      
      // Get categories from venue cuisine or amenities
      let categories: string[] = [];
      if (venue) {
        categories = [
          ...(venue.profile?.cuisine || []),
          ...(venue.profile?.amenities || []),
        ];
      }

      if (categories.length === 0) {
        categories.push('Uncategorized');
      }

      categories.forEach(category => {
        if (!categoryData[category]) {
          categoryData[category] = { count: 0, redemptions: 0, totalOffers: 0 };
        }
        categoryData[category].count++;
        categoryData[category].redemptions += redemptions;
        categoryData[category].totalOffers++;
      });
    });

    return Object.entries(categoryData)
      .map(([category, data]) => ({
        category,
        count: data.totalOffers,
        redemptions: data.redemptions,
        successRate: data.totalOffers > 0 ? (data.redemptions / data.totalOffers) * 100 : 0,
      }))
      .sort((a, b) => b.redemptions - a.redemptions)
      .slice(0, 5);
  };

  const getRecentTopOffersFromOffers = (offers: any[], venues: Venue[]) => {
    const venueMap = new Map(venues.map(v => [v.id, v]));
    
    return offers
      .filter((offer: any) => offer.status === 'active')
      .map((offer: any) => {
        const venue = venueMap.get(offer.venueId);
        const views = offer.analytics?.views || 0;
        const redemptions = offer.analytics?.redemptions || 0;
        
        return {
          id: offer.id,
          title: offer.title || 'Untitled Offer',
          venueName: venue?.name || venue?.businessName || venue?.tradingName || 'Unknown',
          views,
          redemptions,
          conversionRate: views > 0 ? (redemptions / views) * 100 : 0,
          status: offer.status,
        };
      })
      .sort((a, b) => b.redemptions - a.redemptions)
      .slice(0, 5);
  };

  const calculateTopUsersByRedemptions = (offerRedemptions: any[]) => {
    // Group redemptions by userId
    const userRedemptionMap = new Map<string, { userName: string; userEmail: string; count: number }>();
    
    offerRedemptions.forEach((redemption: any) => {
      const userId = redemption.userId;
      const userName = redemption.userName || 'Unknown User';
      const userEmail = redemption.userEmail || '';
      
      if (userId) {
        if (userRedemptionMap.has(userId)) {
          userRedemptionMap.get(userId)!.count++;
        } else {
          userRedemptionMap.set(userId, { userName, userEmail, count: 1 });
        }
      }
    });
    
    // Convert to array and sort by count
    return Array.from(userRedemptionMap.entries())
      .map(([userId, data]) => ({
        userId,
        userName: data.userName,
        userEmail: data.userEmail,
        redemptionCount: data.count,
      }))
      .sort((a, b) => b.redemptionCount - a.redemptionCount)
      .slice(0, 10); // Top 10 users
  };

  const calculateTopVenuesByRedemptions = (offerRedemptions: any[], venues: Venue[]) => {
    // Create a venue map for quick lookup
    const venueMap = new Map(venues.map(v => [v.id, v]));
    
    // Group redemptions by venueId
    const venueRedemptionMap = new Map<string, number>();
    
    offerRedemptions.forEach((redemption: any) => {
      const venueId = redemption.venueId;
      
      if (venueId) {
        venueRedemptionMap.set(venueId, (venueRedemptionMap.get(venueId) || 0) + 1);
      }
    });
    
    // Convert to array and sort by count
    return Array.from(venueRedemptionMap.entries())
      .map(([venueId, count]) => {
        const venue = venueMap.get(venueId);
        return {
          venueId,
          venueName: venue?.name || venue?.businessName || venue?.tradingName || 'Unknown Venue',
          redemptionCount: count,
        };
      })
      .sort((a, b) => b.redemptionCount - a.redemptionCount)
      .slice(0, 10); // Top 10 venues
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-AU').format(Math.round(value));
  };

  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getGrowthColor = (growth: number): 'success' | 'error' | 'default' => {
    if (growth > 0) return 'success';
    if (growth < 0) return 'error';
    return 'default';
  };

  const getGrowthIcon = (growth: number) => {
    return growth >= 0 ? <TrendingUp /> : <TrendingDown />;
  };

  const handleRefresh = () => {
    loadAnalyticsData();
  };

  const handleExport = () => {
    if (!analyticsData) return;
    
    const dataStr = JSON.stringify(analyticsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!analyticsData) {
    return (
      <Box p={3}>
        <Alert severity="info">No analytics data available.</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>Platform Analytics</Typography>
            <Typography variant="body2" color="text.secondary">Comprehensive insights across JamApp</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {/* Quick range chips */}
            {[['7d','7D'],['30d','30D'],['90d','90D'],['1y','1Y'],['all','All'],['custom','Custom']].map(([val, label]) => (
              <Chip
                key={val}
                label={label}
                onClick={() => handleTimeRangeChange(val)}
                color={timeRange === val ? 'primary' : 'default'}
                variant={timeRange === val ? 'filled' : 'outlined'}
                size="small"
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              />
            ))}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={handleRefresh}><Refresh /></IconButton>
            </Tooltip>
            <Tooltip title="Export JSON">
              <IconButton size="small" onClick={handleExport}><GetApp /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Custom date range picker */}
        {showCustomPicker && (
          <Box display="flex" gap={2} alignItems="center" mt={2} flexWrap="wrap">
            <DatePicker
              label="From"
              value={customStart}
              onChange={setCustomStart}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />
            <DatePicker
              label="To"
              value={customEnd}
              onChange={setCustomEnd}
              minDate={customStart || undefined}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyCustomRange}
              disabled={!customStart || !customEnd}
              sx={{ height: 40 }}
            >
              Apply
            </Button>
          </Box>
        )}
      </Box>

      {/* Platform Overview Cards — all-time platform snapshot */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
          Platform Snapshot
        </Typography>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'MRR', value: formatCurrency(analyticsData.platformOverview.totalRevenue),
            sub: 'Current monthly recurring', growth: undefined, icon: <AttachMoney />, color: '#4CAF50' },
          { label: 'Mobile Users', value: formatNumber(analyticsData.platformOverview.totalUsers),
            sub: `${formatNumber(analyticsData.userAnalytics.mobileUsers.authenticated)} registered`, growth: undefined, icon: <PhoneAndroid />, color: '#2196F3' },
          { label: 'Managed Venues', value: formatNumber(analyticsData.platformOverview.totalVenues),
            sub: 'All time total', growth: analyticsData.platformOverview.venueGrowth, icon: <Business />, color: '#FF9800' },
          { label: 'Total Offers', value: formatNumber(analyticsData.platformOverview.totalOffers),
            sub: `${formatNumber(analyticsData.offerAnalytics.activeOffers)} active`, growth: undefined, icon: <LocalOffer />, color: '#F24F2F' },
        ].map((m) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={m.label}>
            <Card sx={{ borderLeft: `4px solid ${m.color}`, '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.2s' }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>{m.label}</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: m.color, my: 0.5 }}>{m.value}</Typography>
                    {m.growth !== undefined && m.growth !== 0 ? (
                      <Chip icon={getGrowthIcon(m.growth)} label={formatPercentage(m.growth)} color={getGrowthColor(m.growth)} size="small" />
                    ) : m.sub ? (
                      <Typography variant="caption" color="text.secondary">{m.sub}</Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ color: m.color, opacity: 0.25, fontSize: 36 }}>{m.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Analytics Events Summary — these DO respond to the date filter */}
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
          Activity for selected period
        </Typography>
        <Chip label={timeRange === 'custom' && customStart && customEnd
          ? `${customStart.format('DD MMM')} – ${customEnd.format('DD MMM')}`
          : timeRange === '7d' ? 'Last 7 days'
          : timeRange === '30d' ? 'Last 30 days'
          : timeRange === '90d' ? 'Last 90 days'
          : timeRange === '1y' ? 'Last year'
          : 'All time'} size="small" color="primary" />
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Events', value: formatNumber(analyticsData.analyticsEvents.totalEvents), sub: 'In selected period', icon: <Analytics />, color: '#9C27B0' },
          { label: 'Views', value: formatNumber(analyticsData.analyticsEvents.totalViews), sub: 'Venue page views', icon: <Analytics />, color: '#2196F3' },
          { label: 'Searches', value: formatNumber(analyticsData.analyticsEvents.totalSearches), sub: 'Venue searches', icon: <LocationOn />, color: '#FF9800' },
          { label: 'Favourites', value: formatNumber(analyticsData.analyticsEvents.totalFavorites), sub: 'Venues saved', icon: <Star />, color: '#F24F2F' },
        ].map((m) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={m.label}>
            <Card sx={{ borderLeft: `4px solid ${m.color}` }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>{m.label}</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: m.color, my: 0.5 }}>{m.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.sub}</Typography>
                  </Box>
                  <Box sx={{ color: m.color, opacity: 0.25, fontSize: 36 }}>{m.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Revenue & Subscription Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Revenue Over Time</Typography>
                <Box display="flex" gap={3}>
                  <Box textAlign="right">
                    <Typography variant="caption" color="text.secondary">Current MRR</Typography>
                    <Typography variant="h6" color="#19d26c">
                      {formatCurrency(analyticsData.revenueMetrics.monthlyRevenue)}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="text.secondary">Avg / Admin</Typography>
                    <Typography variant="h6">
                      {formatCurrency(analyticsData.revenueMetrics.averageRevenuePerAdmin)}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="text.secondary">Subscribers</Typography>
                    <Typography variant="h6">
                      {analyticsData.revenueMetrics.planDistribution.reduce((s, p) => s + p.count, 0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={analyticsData.revenueMetrics.revenueTimeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#19d26c" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#19d26c" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} width={55} />
                  <RechartsTooltip
                    formatter={(value: number, name: string) => [
                      name === 'revenue' ? formatCurrency(value) : value,
                      name === 'revenue' ? 'Revenue' : 'Subscribers'
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#19d26c"
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan Distribution
              </Typography>
              {analyticsData.revenueMetrics.planDistribution.map((plan) => (
                <Box key={plan.planId} mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">
                      {plan.planName} ({plan.count})
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(plan.revenue)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={plan.percentage}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {plan.percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Venues Performance */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Performing Venues
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Venue</TableCell>
                      <TableCell>Admin</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Interactions</TableCell>
                      <TableCell>Views</TableCell>
                      <TableCell>Favorites</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.venuePerformance.topVenues.map((venue, index) => (
                      <TableRow key={venue.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                              #{index + 1}
                            </Typography>
                            <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                              {venue.name.charAt(0)}
                            </Avatar>
                            <Typography variant="body2">{venue.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{venue.adminName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {venue.location}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{formatNumber(venue.totalInteractions)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatNumber(venue.totalViews)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatNumber(venue.totalFavorites)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Geographic Distribution
              </Typography>
              {analyticsData.userAnalytics.geographicDistribution.map((location) => (
                <Box key={location.state} mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" fontWeight="bold">{location.state}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatNumber(location.admins)} admins
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {location.venues} venues • {location.offers} offers
                    </Typography>
                  </Box>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Offer Performance */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Offer Performance Analysis
              </Typography>
              <Grid container spacing={3}>
                {/* Top Users by Redemptions */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Top Users (by redemptions)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Rank</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Redemptions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analyticsData.offerAnalytics.topUsers.map((user, index) => (
                          <TableRow key={user.userId}>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                #{index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <Avatar sx={{ mr: 1, width: 28, height: 28, fontSize: '0.875rem' }}>
                                  {user.userName.charAt(0)}
                                </Avatar>
                                <Typography variant="body2">{user.userName}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                {user.userEmail}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={formatNumber(user.redemptionCount)}
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Top Venues by Redemptions */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Top Venues (by offer performance)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Rank</TableCell>
                          <TableCell>Venue</TableCell>
                          <TableCell>Redemptions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analyticsData.offerAnalytics.topVenuesByRedemptions.map((venue, index) => (
                          <TableRow key={venue.venueId}>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                #{index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <Avatar sx={{ mr: 1, width: 28, height: 28, fontSize: '0.875rem' }}>
                                  {venue.venueName.charAt(0)}
                                </Avatar>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {venue.venueName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={formatNumber(venue.redemptionCount)}
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  </LocalizationProvider>
  );
};

export default SuperAdminAnalytics;